import type {
  ConversationMessage,
  GoogleConnectionStatus,
  ProcessedCapture,
  ProviderHealth
} from "../types";
import { currentSession } from "./supabase";

const DEFAULT_TIMEOUT_MS = 45_000;
/** Must not exceed what the server accepts (MAX_HISTORY_MESSAGES). */
const HISTORY_WINDOW = 30;

/** Fallbacks used when the server could not deliver a JSON error of its own. */
const STATUS_MESSAGES: Record<number, string> = {
  400: "La requête n’a pas pu être lue. Réessayez.",
  401: "Votre session a expiré. Reconnectez-vous avec Google.",
  403: "Cette action n’est pas autorisée pour votre compte.",
  404: "Ce service est introuvable.",
  409: "Reconnectez votre compte Google pour continuer.",
  413: "Le contenu envoyé est trop volumineux.",
  422: "Certaines informations manquent ou sont invalides.",
  429: "Limite de traitement atteinte. Réessayez plus tard.",
  500: "Le service a rencontré une erreur. Réessayez.",
  502: "Le service est momentanément indisponible.",
  503: "Le service n’est pas disponible pour l’instant.",
  504: "Le service met trop de temps à répondre."
};

export type FailureStage =
  | "auth"
  | "input"
  | "antibot"
  | "quota"
  | "configuration"
  | "provider"
  | "destination"
  | "network";

export class ApiError extends Error {
  readonly status: number;
  readonly retryAfterSeconds?: number;
  /** Where the request stopped, when the server said so. */
  readonly stage?: FailureStage;
  /** Whether the message actually reached the AI provider. */
  readonly reachedProvider?: boolean;
  readonly retryable?: boolean;
  /** One sentence explaining what happened. */
  readonly hint?: string;

  constructor(
    status: number,
    message: string,
    detail: {
      retryAfterSeconds?: number;
      stage?: FailureStage;
      reachedProvider?: boolean;
      retryable?: boolean;
      hint?: string;
    } = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfterSeconds = detail.retryAfterSeconds;
    this.stage = detail.stage;
    this.reachedProvider = detail.reachedProvider;
    this.retryable = detail.retryable;
    this.hint = detail.hint;
  }
}

async function authorizationHeaders(): Promise<Record<string, string>> {
  const session = await currentSession();
  return session ? { authorization: `Bearer ${session.access_token}` } : {};
}

/**
 * Reads a JSON payload defensively. An edge error page, an empty 413 or a
 * gateway timeout are not JSON, and parsing them unguarded used to surface a
 * raw `SyntaxError` to the user instead of a usable message.
 */
async function readPayload<T>(response: Response): Promise<Partial<T> & { error?: string }> {
  const text = await response.text().catch(() => "");
  if (!text) return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Partial<T> & { error?: string }) : {};
  } catch {
    return {};
  }
}

async function jsonRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const timeout = AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      signal: timeout,
      headers: {
        "content-type": "application/json",
        ...(await authorizationHeaders()),
        ...options.headers
      }
    });
  } catch (caught) {
    const timedOut = caught instanceof DOMException && caught.name === "TimeoutError";
    throw new ApiError(
      0,
      timedOut
        ? "Le serveur met trop de temps à répondre."
        : "Connexion impossible. Vérifiez votre réseau.",
      {
        stage: "network",
        // The request may or may not have arrived; only a timeout is ambiguous.
        reachedProvider: timedOut,
        retryable: true,
        hint: timedOut
          ? "La demande est peut-être partie sans que la réponse revienne. Votre message est conservé."
          : "La demande n’a pas quitté votre appareil. Rien n’a été consommé."
      }
    );
  }

  const payload = await readPayload<T>(response);
  if (!response.ok) {
    const retryAfter = Number(response.headers.get("retry-after"));
    const detail = payload as {
      error?: string;
      stage?: FailureStage;
      reachedProvider?: boolean;
      retryable?: boolean;
      hint?: string;
    };
    throw new ApiError(
      response.status,
      detail.error || STATUS_MESSAGES[response.status] || "La requête a échoué.",
      {
        retryAfterSeconds:
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
        stage: detail.stage,
        reachedProvider: detail.reachedProvider,
        retryable: detail.retryable,
        hint: detail.hint
      }
    );
  }
  return payload as T;
}

const stampFormat = new Intl.DateTimeFormat("sv-SE", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
});

/**
 * Formats an instant as local wall-clock time, "2026-07-31 09:15".
 *
 * The browser already knows its own zone, so converting here spares the model
 * an offset calculation it would get wrong often enough to matter.
 */
function localStamp(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : stampFormat.format(at).replace(",", "");
}

async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("read_failed"));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
  return dataUrl.split(",")[1] || "";
}

export function fetchHealth(): Promise<ProviderHealth> {
  return jsonRequest("/api/health");
}

export async function processCapture(
  transcript: string,
  audio?: Blob,
  turnstileToken?: string,
  history: ConversationMessage[] = [],
  currentDraft?: ProcessedCapture
): Promise<ProcessedCapture> {
  return jsonRequest("/api/process", {
    method: "POST",
    body: JSON.stringify({
      transcript: transcript.trim() || undefined,
      audioBase64: audio ? await blobToBase64(audio) : undefined,
      mimeType: audio?.type || undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      turnstileToken,
      // Each turn carries when it was said, in the person's own local time.
      // Without it the assistant could not answer "what time was that?" or act
      // on "reprends ce qu'on a fait hier".
      history: history
        .slice(-HISTORY_WINDOW)
        .map(({ role, content, createdAt }) => ({ role, content, at: localStamp(createdAt) })),
      // Only the fields the server understands travel back; the rest would just
      // inflate the prompt.
      currentDraft: currentDraft && {
        title: currentDraft.title,
        body: currentDraft.body,
        destination: currentDraft.destination,
        operation: currentDraft.operation,
        targetTitle: currentDraft.targetTitle,
        event: currentDraft.event
      }
    })
  });
}

export function fetchGoogleStatus(): Promise<GoogleConnectionStatus> {
  return jsonRequest("/api/google/status");
}

export function bootstrapGoogleConnection(
  providerToken: string,
  providerRefreshToken?: string
): Promise<GoogleConnectionStatus> {
  return jsonRequest("/api/google/bootstrap", {
    method: "POST",
    body: JSON.stringify({ providerToken, providerRefreshToken })
  });
}

export function disconnectGoogle(): Promise<GoogleConnectionStatus & { revoked: boolean }> {
  return jsonRequest("/api/google/disconnect", { method: "POST" });
}

export function createGoogleDoc(title: string, body: string) {
  return jsonRequest<{ id: string; url: string; warning?: string }>("/api/google/docs", {
    method: "POST",
    body: JSON.stringify({ title, body, confirmed: true })
  });
}

export function createCalendarEvent(input: {
  title: string;
  description: string;
  start: string;
  end: string;
  timeZone: string;
}) {
  return jsonRequest<{ id: string; url?: string }>("/api/google/calendar", {
    method: "POST",
    body: JSON.stringify({ ...input, confirmed: true })
  });
}
